import { oc } from '@orpc/contract';
import { z } from 'zod';
import { BulkResult, CatalogImage, CatalogListInput, FieldValues, ImageRole, ValidationIssue, Visibility, } from './catalog.js';
import { Id, OkResponse, Timestamp } from './common.js';
import { FilterSpec } from './filter.js';
import { Code } from './meta.js';
const Name = z.string().trim().min(1).max(200);
export const StorageKind = z.enum(['album', 'sheet', 'cell', 'box', 'other']);
export const CollectionNode = z.object({
    id: Id,
    parentId: Id.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    sortKey: z.string(),
    isInbox: z.boolean(),
    /** Экземпляров непосредственно в узле (без вложенных). */
    count: z.number().int(),
    version: z.number().int(),
});
export const LocationNode = z.object({
    id: Id,
    parentId: Id.nullable(),
    name: z.string(),
    kind: StorageKind,
    sortKey: z.string(),
    count: z.number().int(),
    version: z.number().int(),
});
const treeErrors = {
    NOT_FOUND: { status: 404, message: 'not_found' },
    CYCLE: { status: 400, message: 'cycle' },
    INBOX_PROTECTED: { status: 400, message: 'inbox_protected' },
    VERSION_CONFLICT: { status: 409, message: 'version_conflict' },
};
/** Деревья коллекций и мест хранения устроены одинаково (docs/PLAN.md, раздел 4.3). */
export const collectionsContract = {
    tree: oc.route({ method: 'GET', path: '/collections' }).output(z.array(CollectionNode)),
    create: oc
        .route({ method: 'POST', path: '/collections' })
        .errors(treeErrors)
        .input(z.object({
        name: Name,
        parentId: Id.nullable(),
        description: z.string().max(2000).nullable().optional(),
    }))
        .output(CollectionNode),
    update: oc
        .route({ method: 'PATCH', path: '/collections/{id}' })
        .errors(treeErrors)
        .input(z.object({
        id: Id,
        version: z.number().int(),
        name: Name.optional(),
        description: z.string().max(2000).nullable().optional(),
        parentId: Id.nullable().optional(),
        sortKey: z.string().max(64).optional(),
    }))
        .output(CollectionNode),
    /** Содержимое и вложенные коллекции переносятся в родителя (или во «Входящие»). */
    remove: oc
        .route({ method: 'DELETE', path: '/collections/{id}' })
        .errors(treeErrors)
        .input(z.object({ id: Id }))
        .output(OkResponse),
};
export const locationsContract = {
    tree: oc.route({ method: 'GET', path: '/locations' }).output(z.array(LocationNode)),
    create: oc
        .route({ method: 'POST', path: '/locations' })
        .errors(treeErrors)
        .input(z.object({ name: Name, parentId: Id.nullable(), kind: StorageKind }))
        .output(LocationNode),
    update: oc
        .route({ method: 'PATCH', path: '/locations/{id}' })
        .errors(treeErrors)
        .input(z.object({
        id: Id,
        version: z.number().int(),
        name: Name.optional(),
        kind: StorageKind.optional(),
        parentId: Id.nullable().optional(),
        sortKey: z.string().max(64).optional(),
    }))
        .output(LocationNode),
    remove: oc
        .route({ method: 'DELETE', path: '/locations/{id}' })
        .errors(treeErrors)
        .input(z.object({ id: Id }))
        .output(OkResponse),
};
// ---------- экземпляры ----------
export const EventKind = z.enum([
    'acquired',
    'listed',
    'unlisted',
    'sold',
    'exchanged',
    'graded',
    'status_changed',
    'custom',
]);
const Amount = z.string().regex(/^\d{1,12}(\.\d{1,4})?$/, 'money_amount');
const Currency = z.string().regex(/^[A-Z]{3}$/);
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date');
export const EventInput = z.object({
    kind: EventKind,
    occurredOn: IsoDate,
    amount: Amount.nullable().optional(),
    currency: Currency.nullable().optional(),
    fees: Amount.nullable().optional(),
    counterparty: z.string().trim().max(300).nullable().optional(),
    sourceCode: Code.nullable().optional(),
    lotNumber: z.string().trim().max(64).nullable().optional(),
    place: z.string().trim().max(300).nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
});
/** Сумма в базовой валюте владельца на дату (null — курса ещё нет). */
const BaseAmount = {
    baseCurrency: Currency.nullable(),
    amountBase: z.string().nullable(),
};
export const SpecimenEvent = EventInput.extend({ id: Id, createdAt: Timestamp, ...BaseAmount });
export const ValuationInput = z.object({
    valuedOn: IsoDate,
    amount: Amount,
    currency: Currency,
    source: z.string().trim().max(300).nullable().optional(),
});
export const Valuation = ValuationInput.extend({ id: Id, createdAt: Timestamp, ...BaseAmount });
export const Note = z.object({
    id: Id,
    bodyMd: z.string(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
});
export const Tag = z.object({
    id: Id,
    name: z.string(),
    color: z.string().nullable(),
    count: z.number().int(),
});
export const SpecimenListItem = z.object({
    id: Id,
    catalogItemId: Id,
    typeCode: Code,
    collectionId: Id,
    title: z.string(),
    /** Итоговые значения (каталог ← разновидность ← экземпляр) для таблиц и карточек. */
    values: FieldValues,
    thumb: z.string().nullable(),
    tagIds: z.array(Id),
});
export const Specimen = z.object({
    id: Id,
    catalogItem: z.object({ id: Id, typeCode: Code, title: z.string(), visibility: Visibility }),
    variantId: Id.nullable(),
    /** Собственные значения экземпляра: его поля и переопределения каталожных. */
    values: FieldValues,
    /** Итог с источниками — для просмотра. */
    resolved: z.object({
        values: FieldValues,
        sources: z.record(z.string(), z.enum(['catalog', 'variant', 'own'])),
    }),
    images: z.array(CatalogImage),
    catalogImages: z.array(CatalogImage),
    events: z.array(SpecimenEvent),
    /** По дате оценки, от старых к новым. */
    valuations: z.array(Valuation),
    notes: z.array(Note),
    tagIds: z.array(Id),
    version: z.number().int(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
});
export const SpecimenListInput = CatalogListInput.pick({
    q: true,
    filter: true,
    cursor: true,
    limit: true,
}).extend({
    collectionId: Id.optional(),
    includeChildren: z.stringbool().optional(),
    locationId: Id.optional(),
    /** Экземпляры одной записи каталога («у вас есть» на карточке записи). */
    catalogItemId: Id.optional(),
    tagId: Id.optional(),
    /** Код статуса (для «В поиске», «На продаже»). */
    status: Code.optional(),
    sort: z.enum(['title', 'year', 'recent', 'grade']).optional(),
});
const specimenErrors = {
    NOT_FOUND: { status: 404, message: 'not_found' },
    VERSION_CONFLICT: { status: 409, message: 'version_conflict' },
    VALIDATION_FAILED: {
        status: 400,
        message: 'validation_failed',
        data: z.object({ issues: z.array(ValidationIssue) }),
    },
};
export const specimensContract = {
    list: oc
        .route({ method: 'GET', path: '/specimens' })
        .errors({ VALIDATION_FAILED: specimenErrors.VALIDATION_FAILED })
        .input(SpecimenListInput)
        .output(z.object({ items: z.array(SpecimenListItem), nextCursor: z.string().nullable() })),
    get: oc
        .route({ method: 'GET', path: '/specimens/{id}' })
        .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
        .input(z.object({ id: Id }))
        .output(Specimen),
    create: oc
        .route({ method: 'POST', path: '/specimens' })
        .errors(specimenErrors)
        .input(z.object({
        catalogItemId: Id,
        variantId: Id.nullable().optional(),
        values: FieldValues,
        /** Покупка одним запросом с созданием (блок «Покупка» в форме). */
        acquisition: EventInput.omit({ kind: true }).optional(),
        tagIds: z.array(Id).max(50).optional(),
    }))
        .output(Specimen),
    update: oc
        .route({ method: 'PATCH', path: '/specimens/{id}' })
        .errors(specimenErrors)
        .input(z.object({
        id: Id,
        version: z.number().int(),
        variantId: Id.nullable().optional(),
        values: FieldValues,
        tagIds: z.array(Id).max(50).optional(),
    }))
        .output(Specimen),
    remove: oc
        .route({ method: 'DELETE', path: '/specimens/{id}' })
        .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
        .input(z.object({ id: Id }))
        .output(OkResponse),
    duplicate: oc
        .route({ method: 'POST', path: '/specimens/{id}/duplicate' })
        .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
        .input(z.object({ id: Id }))
        .output(Specimen),
    /** Массовые операции: перенос, статус, теги. */
    bulk: oc
        .route({ method: 'POST', path: '/specimens/bulk' })
        .errors(specimenErrors)
        .input(z.object({
        ids: z.array(Id).min(1).max(500),
        collectionId: Id.optional(),
        status: Code.optional(),
        addTagIds: z.array(Id).max(50).optional(),
        /** Значения полей экземпляра — одинаковые для всех выбранных (null очищает поле). */
        values: FieldValues.optional(),
    }))
        .output(BulkResult),
    events: {
        create: oc
            .route({ method: 'POST', path: '/specimens/{specimenId}/events' })
            .errors(specimenErrors)
            .input(EventInput.extend({ specimenId: Id }))
            .output(SpecimenEvent),
        update: oc
            .route({ method: 'PATCH', path: '/events/{id}' })
            .errors(specimenErrors)
            .input(EventInput.partial().extend({ id: Id }))
            .output(SpecimenEvent),
        remove: oc
            .route({ method: 'DELETE', path: '/events/{id}' })
            .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
            .input(z.object({ id: Id }))
            .output(OkResponse),
    },
    valuations: {
        create: oc
            .route({ method: 'POST', path: '/specimens/{specimenId}/valuations' })
            .errors(specimenErrors)
            .input(ValuationInput.extend({ specimenId: Id }))
            .output(Valuation),
        remove: oc
            .route({ method: 'DELETE', path: '/valuations/{id}' })
            .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
            .input(z.object({ id: Id }))
            .output(OkResponse),
    },
    notes: {
        create: oc
            .route({ method: 'POST', path: '/specimens/{specimenId}/notes' })
            .errors(specimenErrors)
            .input(z.object({ specimenId: Id, bodyMd: z.string().trim().min(1).max(20_000) }))
            .output(Note),
        update: oc
            .route({ method: 'PATCH', path: '/notes/{id}' })
            .errors(specimenErrors)
            .input(z.object({ id: Id, bodyMd: z.string().trim().min(1).max(20_000) }))
            .output(Note),
        remove: oc
            .route({ method: 'DELETE', path: '/notes/{id}' })
            .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
            .input(z.object({ id: Id }))
            .output(OkResponse),
    },
    images: {
        attach: oc
            .route({ method: 'POST', path: '/specimens/{specimenId}/images' })
            .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
            .input(z.object({ specimenId: Id, mediaId: Id, role: ImageRole }))
            .output(CatalogImage),
        /**
         * Фото записи каталога → фото этого экземпляра (с ролями, поворотом и кадром): дальше их
         * можно править независимо от каталога. Уже скопированные пропускаются.
         */
        copyFromCatalog: oc
            .route({ method: 'POST', path: '/specimens/{specimenId}/images/from-catalog' })
            .errors({ NOT_FOUND: specimenErrors.NOT_FOUND })
            .input(z.object({ specimenId: Id }))
            .output(z.array(CatalogImage)),
    },
};
// ---------- продавцы и площадки ----------
export const Counterparty = z.object({
    id: Id,
    name: z.string(),
    /** Код из набора acquisition_source: аукцион, магазин, дилер… */
    kind: Code.nullable(),
    url: z.string().nullable(),
    note: z.string().nullable(),
    /** Событий (покупок, продаж, обменов) с этим продавцом. */
    events: z.number().int(),
    /** Потрачено у него в базовой валюте (покупки с комиссией) и выручено (продажи). */
    spent: z.string(),
    earned: z.string(),
    baseCurrency: Currency,
    lastOn: IsoDate.nullable(),
});
const CounterpartyFields = z.object({
    name: z.string().trim().min(1).max(200),
    kind: Code.nullable().optional(),
    url: z
        .url({ protocol: /^https?$/ })
        .max(500)
        .nullable()
        .optional(),
    note: z.string().trim().max(2000).nullable().optional(),
});
export const counterpartiesContract = {
    list: oc.route({ method: 'GET', path: '/counterparties' }).output(z.array(Counterparty)),
    create: oc
        .route({ method: 'POST', path: '/counterparties' })
        .errors({ NAME_TAKEN: { status: 409, message: 'name_taken' } })
        .input(CounterpartyFields)
        .output(Counterparty),
    /** Переименование меняет подпись и в уже записанных событиях. */
    update: oc
        .route({ method: 'PATCH', path: '/counterparties/{id}' })
        .errors({
        NAME_TAKEN: { status: 409, message: 'name_taken' },
        NOT_FOUND: { status: 404, message: 'not_found' },
    })
        .input(CounterpartyFields.partial().extend({ id: Id }))
        .output(Counterparty),
    /** События остаются, с подписью-текстом; из справочника запись уходит. */
    remove: oc
        .route({ method: 'DELETE', path: '/counterparties/{id}' })
        .input(z.object({ id: Id }))
        .output(OkResponse),
};
export const tagsContract = {
    list: oc.route({ method: 'GET', path: '/tags' }).output(z.array(Tag)),
    create: oc
        .route({ method: 'POST', path: '/tags' })
        .errors({ NAME_TAKEN: { status: 409, message: 'name_taken' } })
        .input(z.object({
        name: z.string().trim().min(1).max(60),
        color: z.string().max(16).nullable().optional(),
    }))
        .output(Tag),
    update: oc
        .route({ method: 'PATCH', path: '/tags/{id}' })
        .errors({
        NAME_TAKEN: { status: 409, message: 'name_taken' },
        NOT_FOUND: { status: 404, message: 'not_found' },
    })
        .input(z.object({
        id: Id,
        name: z.string().trim().min(1).max(60).optional(),
        color: z.string().max(16).nullable().optional(),
    }))
        .output(Tag),
    remove: oc
        .route({ method: 'DELETE', path: '/tags/{id}' })
        .input(z.object({ id: Id }))
        .output(OkResponse),
};
export const SavedFilter = z.object({
    id: Id,
    name: z.string(),
    filter: FilterSpec,
    /** Дополнительные параметры списка (статус, тег, коллекция). */
    params: z.object({
        status: Code.optional(),
        tagId: Id.optional(),
        collectionId: Id.optional(),
        includeChildren: z.boolean().optional(),
    }),
    sortKey: z.string(),
});
export const savedFiltersContract = {
    list: oc.route({ method: 'GET', path: '/saved-filters' }).output(z.array(SavedFilter)),
    create: oc
        .route({ method: 'POST', path: '/saved-filters' })
        .input(SavedFilter.pick({ name: true, filter: true, params: true }).extend({ name: Name }))
        .output(SavedFilter),
    update: oc
        .route({ method: 'PATCH', path: '/saved-filters/{id}' })
        .input(z.object({
        id: Id,
        name: Name.optional(),
        filter: FilterSpec.optional(),
        params: SavedFilter.shape.params.optional(),
    }))
        .output(SavedFilter),
    remove: oc
        .route({ method: 'DELETE', path: '/saved-filters/{id}' })
        .input(z.object({ id: Id }))
        .output(OkResponse),
};
export const StatsGroup = z.enum(['type', 'issuer', 'status', 'collection']);
export const StatsBreakdownGroup = z.enum([
    'type',
    'issuer',
    'year',
    'collection',
    'status',
    'counterparty',
]);
/** Выборка для статистики — те же параметры, что у списка экземпляров. */
export const StatsScope = SpecimenListInput.pick({
    collectionId: true,
    includeChildren: true,
    tagId: true,
    status: true,
    filter: true,
});
const Money = z.string();
export const StatsSummary = z.object({
    baseCurrency: Currency,
    /** Потрачено на покупки (с комиссиями) — по всем экземплярам, включая проданные. */
    spent: Money,
    /** Текущая стоимость: последняя оценка экземпляров «в наличии». */
    value: Money,
    /** Продажи: выручка за вычетом комиссий, себестоимость проданного и прибыль. */
    soldRevenue: Money,
    soldCost: Money,
    soldProfit: Money,
    specimens: z.number().int(),
    /** Экземпляров «в наличии» (с учётом количества). */
    owned: z.number().int(),
    valued: z.number().int(),
    /** Суммы, для которых нет курса (не вошли в итоги). */
    unconverted: z.number().int(),
});
export const StatsRow = z.object({
    key: z.string().nullable(),
    label: z.string(),
    count: z.number().int(),
    spent: Money,
    value: Money,
});
export const statsContract = {
    summary: oc
        .route({ method: 'GET', path: '/stats/summary' })
        .errors({ VALIDATION_FAILED: specimenErrors.VALIDATION_FAILED })
        .input(StatsScope)
        .output(StatsSummary),
    breakdown: oc
        .route({ method: 'GET', path: '/stats/breakdown' })
        .errors({ VALIDATION_FAILED: specimenErrors.VALIDATION_FAILED })
        .input(StatsScope.extend({ groupBy: StatsBreakdownGroup }))
        .output(z.array(StatsRow)),
    /** Счётчики экземпляров (количество с учётом quantity) по разрезу. */
    counts: oc
        .route({ method: 'GET', path: '/stats/counts' })
        .input(z.object({ groupBy: StatsGroup }))
        .output(z.array(z.object({ key: z.string().nullable(), label: z.string(), count: z.number().int() }))),
};
//# sourceMappingURL=collections.js.map